import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  archiveAdminTeam,
  carryForwardAdminTeam,
  closeAdminSeason,
  createAdminTeam,
  createAthlete,
  createAthleteLink,
  deleteAthlete,
  deleteAthleteLink,
  getAdminAccessRequests,
  getAdminSeasons,
  getAthlete,
  getAthletes,
  getClubLiveConfig,
  getClubLiveStatus,
  getClubOverview,
  getTranscodeProgress,
  mergeAthletes,
  updateAdminTeam,
  updateAthlete,
} from "@/lib/api";
import { queryClient, unscopedKey } from "@/lib/queryClient";
import {
  adminAthleteKeys,
  invalidateAthleteReads,
} from "@/features/athletes/hooks/useAthletes";
import type {
  AthleteCreate,
  AthleteDetail,
  AthleteDirectoryEntry,
  AthleteDirectoryStatusFilter,
  AthleteLinkCreate,
  AthleteMergeRequest,
  AthleteUpdate,
  CarryForwardTeamRequest,
  ClubLiveConfigPayload,
  ClubLiveStatusPayload,
  ClubOverviewPayload,
  CreateAdminTeamRequest,
  TranscodeProgressMap,
  UpdateAdminTeamRequest,
} from "@/lib/types";

export { adminAthleteKeys };

/**
 * Club console TanStack Query hooks (admin-console-split).
 *
 * The club-scoped polls behind /admin: the Overview payload, the slim Live
 * page's status/config, and transcode progress for the match library. The
 * operator polls live in features/super-admin/hooks/useOperatorData.ts.
 *
 * All polls:
 *   - pause when the document is hidden (`refetchIntervalInBackground:
 *     false`) — the operator isn't watching while the tab is in the
 *     background, and the 10 s + 5 s loops would otherwise hammer the
 *     backend from every parked tab
 *   - share a single cache key per endpoint so navigation between
 *     /admin/* sub-routes doesn't refetch the same data
 *   - read the (already-injected) Bearer auth token via `apiFetch`'s
 *     normal flow — no extra header plumbing here
 *
 * No `staleTime` is set here: every admin signal is intentionally
 * non-stale (the strip should reflect the latest tick), so a manual
 * refetch on tab-focus re-runs the queryFn immediately.
 */

/** Club console Overview payload (admin-console-split): club KPIs +
 * per-team rollup + this club's slot/upload signals. The club-scoped
 * replacement for the diagnostics poll on `/admin/overview` — private
 * unscoped domain, evicted on logout. */
export function useClubOverview(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<ClubOverviewPayload>({
    queryKey: unscopedKey("club-overview"),
    queryFn: getClubOverview,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Own-club per-team live state + attributable viewer counts (club Live). */
export function useClubLiveStatus(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<ClubLiveStatusPayload>({
    queryKey: unscopedKey("club-live-status"),
    queryFn: getClubLiveStatus,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Keyless club live config (enabled / RTMP base / offline message). */
export function useClubLiveConfig(options?: { enabled?: boolean }) {
  return useQuery<ClubLiveConfigPayload>({
    queryKey: unscopedKey("club-live-config"),
    queryFn: getClubLiveConfig,
    enabled: options?.enabled ?? true,
  });
}

/** Live transcode progress — authenticated + host-club-filtered on the
 * server. Polled only when `enabled` is true (the caller gates on "any
 * slot currently transcoding" so an idle library page doesn't poll). */
export function useTranscodeProgress(options: {
  enabled: boolean;
  refetchIntervalMs?: number;
}) {
  const interval = options.refetchIntervalMs ?? 5_000;
  return useQuery<TranscodeProgressMap>({
    queryKey: unscopedKey("transcode-progress"),
    queryFn: getTranscodeProgress,
    refetchInterval: options.enabled ? interval : false,
    refetchIntervalInBackground: false,
    enabled: options.enabled,
  });
}

/** Same authorized request list/cache as People; unknown remains unknown on failure. */
export function useClubAccessRequests(enabled: boolean) {
  return useQuery({
    queryKey: unscopedKey("admin-access-requests"),
    queryFn: getAdminAccessRequests,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useClubSeasons(enabled: boolean) {
  return useQuery({
    queryKey: unscopedKey("admin-seasons"),
    queryFn: getAdminSeasons,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// #908 — season teams + the club athlete directory
// (docs/plans/athlete-directory-season-teams-plan.md §6, §7). Keys are the
// unscoped-private `admin-*` domains the pages already use; the athlete
// key factories and the cross-surface invalidation helper live in
// `features/athletes/hooks/useAthletes.ts` (re-exported above) so the
// coach and account surfaces refresh after a club-console write too.
// ---------------------------------------------------------------------------

/** Every club-console reader of `GET /api/admin/teams` keys its own copy
 * (Teams / People / Matches pages, the team switcher, the Live page). */
export const adminTeamKeys = {
  list: () => unscopedKey("admin-teams"),
  switcher: () => unscopedKey("admin-teams-switcher"),
  live: () => unscopedKey("admin-teams-live"),
};

export const adminSeasonKeys = {
  list: () => unscopedKey("admin-seasons"),
};

/** Refresh every read of the team list after a team is created, edited,
 * carried forward or archived, plus the Overview rollup (teams per season,
 * quota) and `/api/me` — its `teams[]` carries each team's `season` and
 * `status`, and the scope pickers group on them. */
export async function invalidateAdminTeamReads(): Promise<void> {
  await Promise.all(
    [
      adminTeamKeys.list(),
      adminTeamKeys.switcher(),
      adminTeamKeys.live(),
      unscopedKey("club-overview"),
      unscopedKey("me"),
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

/** `/admin/athletes` directory search. `status` narrows to one status;
 * `null` lists active + archived (merged athletes only with `"merged"`). */
export function useAdminAthletes(
  query: string,
  status: AthleteDirectoryStatusFilter | null = null,
  options?: { enabled?: boolean },
) {
  return useQuery<AthleteDirectoryEntry[]>({
    queryKey: adminAthleteKeys.list(query, status),
    queryFn: () => getAthletes({ query, status: status ?? undefined }),
    enabled: options?.enabled ?? true,
    // Keep the previous list on screen while the next keystroke's loads.
    placeholderData: keepPreviousData,
  });
}

/** `/admin/athletes/$athleteId` — athlete, assignments, links (when
 * `can_manage_links`) and merge audit (when `can_merge`). */
export function useAdminAthlete(
  athleteId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<AthleteDetail>({
    queryKey: adminAthleteKeys.detail(athleteId ?? ""),
    queryFn: () => getAthlete(athleteId as string),
    enabled: !!athleteId && (options?.enabled ?? true),
  });
}

/** `POST /api/athletes` — a club athlete with no assignment yet. */
export function useAdminCreateAthlete() {
  return useMutation({
    mutationFn: (body: AthleteCreate) => createAthlete(body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `PATCH /api/athletes/{id}` — names / birth year; `status` (archive) is
 * club-admin only. A display-name change propagates to active roster rows. */
export function useAdminUpdateAthlete() {
  return useMutation({
    mutationFn: ({ athleteId, body }: { athleteId: string; body: AthleteUpdate }) =>
      updateAthlete(athleteId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `DELETE /api/athletes/{id}` — guarded delete (club admin; archived and
 * nothing attached). The deleted athlete's detail read is never refetched
 * (it would 404); it is dropped from the cache once unobserved. */
export function useAdminDeleteAthlete() {
  return useMutation({
    mutationFn: (athleteId: string) => deleteAthlete(athleteId),
    onSuccess: async (_data, athleteId) => {
      await invalidateAthleteReads({ deletedAthleteId: athleteId });
      // Unobserved copies go now; a still-mounted detail page keeps its
      // (stale) copy until it navigates away and it is collected.
      queryClient.removeQueries({
        queryKey: adminAthleteKeys.detail(athleteId),
        type: "inactive",
      });
    },
  });
}

/** `POST /api/athletes/{id}/links` — link an account (self / guardian /
 * family); projected onto every assignment. */
export function useAdminLinkAthlete() {
  return useMutation({
    mutationFn: ({ athleteId, body }: { athleteId: string; body: AthleteLinkCreate }) =>
      createAthleteLink(athleteId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `DELETE /api/athletes/{id}/links/{link_id}` — revoke a link. */
export function useAdminUnlinkAthlete() {
  return useMutation({
    mutationFn: ({ athleteId, linkId }: { athleteId: string; linkId: number }) =>
      deleteAthleteLink(athleteId, linkId),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/athletes/{survivor_id}/merge` — reviewed merge (D10, club
 * admin only); 409 when both athletes hold an assignment on one team. */
export function useAdminMergeAthletes() {
  return useMutation({
    mutationFn: ({
      survivorAthleteId,
      body,
    }: {
      survivorAthleteId: string;
      body: AthleteMergeRequest;
    }) => mergeAthletes(survivorAthleteId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/admin/teams` — create a team, optionally inside a season
 * (`season_id`) and with lineage (`carried_from_team_id`). */
export function useCreateAdminTeam() {
  return useMutation({
    mutationFn: (body: CreateAdminTeamRequest) => createAdminTeam(body),
    onSuccess: () => invalidateAdminTeamReads(),
  });
}

/** `PATCH /api/admin/teams/{id}` — rename / game format / visibility, plus
 * the #908 fields: `season_id` (write-once: seasons a legacy team) and
 * `status`. Send only the fields being changed. */
export function useUpdateAdminTeam() {
  return useMutation({
    mutationFn: ({ teamId, body }: { teamId: string; body: UpdateAdminTeamRequest }) =>
      updateAdminTeam(teamId, body),
    onSuccess: () => invalidateAdminTeamReads(),
  });
}

/** `POST /api/admin/teams/{id}/carry-forward` — a new season team carried
 * forward from `teamId` (lineage, copied settings, new stream key, no
 * staff, no roster). The result's `source_roster` feeds the bulk roster
 * step (`useBulkAssignments`). */
export function useCarryForwardTeam() {
  return useMutation({
    mutationFn: ({ teamId, body }: { teamId: string; body: CarryForwardTeamRequest }) =>
      carryForwardAdminTeam(teamId, body),
    onSuccess: () => invalidateAdminTeamReads(),
  });
}

/** `POST /api/admin/teams/{id}/archive` — archive a season team; nothing
 * is deleted (D5). Linked-athlete rows carry the team status, so the
 * athlete reads refresh as well. */
export function useArchiveTeam() {
  return useMutation({
    mutationFn: (teamId: string) => archiveAdminTeam(teamId),
    onSuccess: () =>
      Promise.all([invalidateAdminTeamReads(), invalidateAthleteReads()]).then(
        () => undefined,
      ),
  });
}

/** `POST /api/admin/seasons/{id}/close` — archive every team of the
 * season (idempotent); frees the club's team quota (D13). */
export function useCloseSeason() {
  return useMutation({
    mutationFn: (seasonId: string) => closeAdminSeason(seasonId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminSeasonKeys.list() }),
        invalidateAdminTeamReads(),
        invalidateAthleteReads(),
      ]).then(() => undefined),
  });
}
